import { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { Toaster } from "sonner";
import { Download, FileText, FileSpreadsheet, BarChart3, Users, CalendarRange, UserCog, Building2 } from "lucide-react";
import { downloadFile } from "../../lib/download";

const REPORTS = [
    { key: "bookings", label: "Bookings", desc: "All bookings with payment, slot, and status — filterable by date range.", icon: BarChart3, baseUrl: "/admin/reports/bookings", useDateRange: true },
    { key: "customers", label: "Customers", desc: "Players with booking counts, loyalty points, and contact info.", icon: Users, baseUrl: "/admin/reports/customers", useDateRange: false },
    { key: "revenue", label: "Revenue", desc: "Day-wise revenue with online vs offline split and pending balance.", icon: CalendarRange, baseUrl: "/admin/reports/revenue", useDateRange: true },
    { key: "events", label: "Events", desc: "Tournaments with team registration counts and status (Active / Few spots left / Completed).", icon: BarChart3, baseUrl: "/admin/reports/events", useDateRange: false },
];

const FORMATS = {
    csv: { label: "CSV", icon: Download, ext: "csv" },
    pdf: { label: "PDF", icon: FileText, ext: "pdf" },
    xlsx: { label: "Excel", icon: FileSpreadsheet, ext: "xlsx" },
};

export default function AdminReports() {
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [managers, setManagers] = useState([]);
    const [managerId, setManagerId] = useState("");
    const [centres, setCentres] = useState([]);
    const [revenueCentreId, setRevenueCentreId] = useState("");
    const [managerStatus, setManagerStatus] = useState("all");
    // Iter 33 — Per-Centre Revenue Report (mirrors Manager Report)
    const [centreId, setCentreId] = useState("");
    const [centreStatus, setCentreStatus] = useState("all");
    const [centreSummary, setCentreSummary] = useState(null);

    useEffect(() => {
        api.get("/admin/managers/with-stats").then(({ data }) => {
            setManagers(data);
            if (data.length) setManagerId(data[0].id);
        });
        api.get("/centres").then(({ data }) => {
            setCentres(data || []);
            if (data && data.length) setCentreId(data[0].id);
        });
    }, []);

    // Iter 33 — refresh centre summary tile whenever centre/date/status changes
    useEffect(() => {
        let alive = true;
        const run = async () => {
            if (!centreId) {
                if (alive) setCentreSummary(null);
                return;
            }
            const params = { centre_id: centreId };
            if (start) params.start_date = start;
            if (end) params.end_date = end;
            if (centreStatus && centreStatus !== "all") params.status = centreStatus;
            try {
                const { data } = await api.get("/admin/reports/centre-report", { params });
                if (alive) setCentreSummary(data?.summary || null);
            } catch (_) {
                if (alive) setCentreSummary(null);
            }
        };
        run();
        return () => { alive = false; };
    }, [centreId, start, end, centreStatus]);

    const handleDownload = (r, fmt) => {
        const params = {};
        if (r.useDateRange) {
            if (start) params.start_date = start;
            if (end) params.end_date = end;
        }
        // Iter 32 / A5 — revenue report supports an optional centre filter
        if (r.key === "revenue" && revenueCentreId) {
            params.centre_id = revenueCentreId;
        }
        downloadFile(`${r.baseUrl}.${fmt.ext}`, `${r.key}.${fmt.ext}`, params);
    };

    const downloadManager = (fmt) => {
        if (!managerId) return;
        const m = managers.find(m => m.id === managerId);
        const slug = (m?.name || "manager").toLowerCase().replace(/\s+/g, "_");
        const params = { manager_id: managerId };
        if (start) params.start_date = start;
        if (end) params.end_date = end;
        // Iter 32 / A2 — optional status filter (all / paid / cancelled / refunded)
        if (managerStatus && managerStatus !== "all") params.status = managerStatus;
        downloadFile(`/admin/reports/manager.${fmt.ext}`, `manager-${slug}.${fmt.ext}`, params);
    };

    // Iter 33 — Per-Centre Revenue Report download
    const downloadCentre = (fmt) => {
        if (!centreId) return;
        const c = centres.find(c => c.id === centreId);
        const slug = (c?.name || "centre").toLowerCase().replace(/\s+/g, "_");
        const params = { centre_id: centreId };
        if (start) params.start_date = start;
        if (end) params.end_date = end;
        if (centreStatus && centreStatus !== "all") params.status = centreStatus;
        downloadFile(`/admin/reports/centre.${fmt.ext}`, `centre-${slug}.${fmt.ext}`, params);
    };

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8">
                <h1 className="font-display text-3xl font-bold uppercase text-white">Reports & Exports</h1>
                <p className="text-slate-400 text-sm mt-1">Download CSV, PDF or Excel for accounting, marketing, and analysis.</p>

                <div className="mt-6 bg-slate-900 border border-slate-800 rounded-lg p-4 sm:p-5 flex flex-wrap items-end gap-3">
                    {/* Iter 39 — Preset date-range chips (Day / Week / Month / Year / Custom).
                        Selecting a preset auto-fills the From/To inputs; "Custom" just keeps
                        whatever is currently in the inputs. */}
                    <div className="w-full">
                        <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Quick Range</label>
                        <div className="mt-1 flex flex-wrap gap-1.5" data-testid="report-range-presets">
                            {[
                                { key: "today", label: "Day" },
                                { key: "week", label: "Week" },
                                { key: "month", label: "Month" },
                                { key: "year", label: "Year" },
                                { key: "custom", label: "Custom" },
                            ].map(p => (
                                <button key={p.key} data-testid={`report-range-${p.key}`}
                                    onClick={() => {
                                        const today = new Date();
                                        const yyyymmdd = (d) => d.toISOString().slice(0, 10);
                                        if (p.key === "today") { setStart(yyyymmdd(today)); setEnd(yyyymmdd(today)); }
                                        else if (p.key === "week") {
                                            const w = new Date(today); w.setDate(w.getDate() - 6);
                                            setStart(yyyymmdd(w)); setEnd(yyyymmdd(today));
                                        }
                                        else if (p.key === "month") {
                                            const m = new Date(today.getFullYear(), today.getMonth(), 1);
                                            setStart(yyyymmdd(m)); setEnd(yyyymmdd(today));
                                        }
                                        else if (p.key === "year") {
                                            const y = new Date(today.getFullYear(), 0, 1);
                                            setStart(yyyymmdd(y)); setEnd(yyyymmdd(today));
                                        }
                                        // "custom" → no change; user edits inputs below
                                    }}
                                    className="px-3 h-9 text-[10px] font-bold uppercase tracking-widest rounded border border-slate-700 text-slate-300 hover:border-[#00FF66] hover:text-[#00FF66] transition">
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">From</label>
                        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start"
                            className="block mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm" />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">To</label>
                        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end"
                            className="block mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm" />
                    </div>
                    <button onClick={() => { setStart(""); setEnd(""); }} data-testid="report-clear"
                        className="h-10 px-4 border border-slate-700 rounded text-slate-300 text-xs font-bold uppercase tracking-widest hover:border-[#00FF66]">
                        Clear
                    </button>
                    <div>
                        <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Centre (Revenue only)</label>
                        <select value={revenueCentreId} onChange={(e) => setRevenueCentreId(e.target.value)} data-testid="report-revenue-centre"
                            className="block mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm min-w-[180px]">
                            <option value="">All Centres</option>
                            {centres.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <p className="text-[11px] text-slate-500 ml-auto">Date range applies to <b>Bookings</b>, <b>Revenue</b>, <b>Manager</b>, and <b>Centre</b>.</p>
                </div>

                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {REPORTS.map(r => {
                        const Icon = r.icon;
                        return (
                            <div key={r.key} className="bg-slate-900 border border-slate-800 rounded-lg p-5 card-hover" data-testid={`report-${r.key}`}>
                                <div className="flex items-start gap-3">
                                    <div className="flex-1">
                                        <div className="inline-flex items-center gap-2 text-[#00FF66] mb-2">
                                            <Icon className="w-4 h-4" />
                                            <span className="text-[10px] font-bold uppercase tracking-[0.25em]">Report</span>
                                        </div>
                                        <h3 className="font-display text-xl font-bold uppercase text-white">{r.label}</h3>
                                        <p className="text-sm text-slate-400 mt-1">{r.desc}</p>
                                    </div>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {["csv", "pdf", "xlsx"].map(fk => {
                                        const F = FORMATS[fk];
                                        const FI = F.icon;
                                        return (
                                            <button key={fk} onClick={() => handleDownload(r, F)} data-testid={`download-${r.key}-${fk}`}
                                                className={`inline-flex items-center gap-2 px-3 h-9 rounded text-[11px] font-bold uppercase tracking-widest ${
                                                    fk === "xlsx" ? "bg-[#00FF66] text-black hover:bg-[#00cc52]" :
                                                    fk === "pdf" ? "bg-slate-200 text-slate-900 hover:bg-white" :
                                                    "bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700"
                                                }`}>
                                                <FI className="w-3.5 h-3.5" /> {F.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}

                    {/* Manager-specific report card */}
                    <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 card-hover lg:col-span-2" data-testid="report-manager">
                        <div className="flex items-start justify-between flex-wrap gap-3">
                            <div className="flex-1">
                                <div className="inline-flex items-center gap-2 text-pink-400 mb-2">
                                    <UserCog className="w-4 h-4" />
                                    <span className="text-[10px] font-bold uppercase tracking-[0.25em]">Manager Report</span>
                                </div>
                                <h3 className="font-display text-xl font-bold uppercase text-white">Per-Manager Breakdown</h3>
                                <p className="text-sm text-slate-400 mt-1">All bookings the chosen manager either booked or collected balance for, with split totals.</p>
                            </div>
                            <div className="flex flex-wrap items-end gap-3">
                                <div>
                                    <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Manager</label>
                                    <select value={managerId} onChange={(e) => setManagerId(e.target.value)} data-testid="manager-select"
                                        className="block mt-1 min-w-[220px] h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm">
                                        {managers.length === 0 ? (
                                            <option>No managers</option>
                                        ) : managers.map(m => (
                                            <option key={m.id} value={m.id}>
                                                {m.name} · Booked ₹{(m.total_booked_amount ?? 0).toLocaleString("en-IN")} · Collected ₹{(m.total_collected_amount ?? 0).toLocaleString("en-IN")}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Status</label>
                                    <select value={managerStatus} onChange={(e) => setManagerStatus(e.target.value)} data-testid="manager-status-filter"
                                        className="block mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm">
                                        <option value="all">All</option>
                                        <option value="paid">Paid</option>
                                        <option value="cancelled">Cancelled</option>
                                        <option value="refunded">Refunded</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                            <button onClick={() => downloadManager(FORMATS.pdf)} disabled={!managerId} data-testid="download-manager-pdf"
                                className="inline-flex items-center gap-2 px-3 h-9 rounded text-[11px] font-bold uppercase tracking-widest bg-slate-200 text-slate-900 hover:bg-white disabled:opacity-40">
                                <FileText className="w-3.5 h-3.5" /> PDF
                            </button>
                            <button onClick={() => downloadManager(FORMATS.xlsx)} disabled={!managerId} data-testid="download-manager-xlsx"
                                className="inline-flex items-center gap-2 px-3 h-9 rounded text-[11px] font-bold uppercase tracking-widest bg-[#00FF66] text-black hover:bg-[#00cc52] disabled:opacity-40">
                                <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
                            </button>
                        </div>
                        {(() => {
                            const m = managers.find(x => x.id === managerId);
                            if (!m) return null;
                            const booked = (m.total_booked_amount ?? 0);
                            const collected = (m.total_collected_amount ?? 0);
                            return (
                                <div className="mt-4 grid grid-cols-2 gap-3" data-testid="manager-summary">
                                    <div className="bg-slate-950 border border-slate-800 rounded p-3">
                                        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Total Booking Amount</div>
                                        <div className="mt-1 font-display text-2xl font-bold text-white" data-testid="mgr-total-booked">₹{booked.toLocaleString("en-IN")}</div>
                                    </div>
                                    <div className="bg-slate-950 border border-[#00FF66]/40 rounded p-3">
                                        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Manager Collected</div>
                                        <div className="mt-1 font-display text-2xl font-bold text-[#00FF66]" data-testid="mgr-total-collected">₹{collected.toLocaleString("en-IN")}</div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>

                    {/* Iter 33 — Per-Centre Revenue Report (mirrors Manager Report) */}
                    <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 card-hover lg:col-span-2" data-testid="report-centre">
                        <div className="flex items-start justify-between flex-wrap gap-3">
                            <div className="flex-1">
                                <div className="inline-flex items-center gap-2 text-cyan-400 mb-2">
                                    <Building2 className="w-4 h-4" />
                                    <span className="text-[10px] font-bold uppercase tracking-[0.25em]">Centre Report</span>
                                </div>
                                <h3 className="font-display text-xl font-bold uppercase text-white">Per-Centre Revenue Report</h3>
                                <p className="text-sm text-slate-400 mt-1">Every booking at the chosen centre with User Paid / Admin Collected / Manager Collected split, exportable as PDF or Excel.</p>
                            </div>
                            <div className="flex flex-wrap items-end gap-3">
                                <div>
                                    <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Centre</label>
                                    <select value={centreId} onChange={(e) => setCentreId(e.target.value)} data-testid="centre-select"
                                        className="block mt-1 min-w-[220px] h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm">
                                        {centres.length === 0 ? (
                                            <option>No centres</option>
                                        ) : centres.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Status</label>
                                    <select value={centreStatus} onChange={(e) => setCentreStatus(e.target.value)} data-testid="centre-status-filter"
                                        className="block mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm">
                                        <option value="all">All</option>
                                        <option value="paid">Paid</option>
                                        <option value="cancelled">Cancelled</option>
                                        <option value="refunded">Refunded</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                            <button onClick={() => downloadCentre(FORMATS.pdf)} disabled={!centreId} data-testid="download-centre-pdf"
                                className="inline-flex items-center gap-2 px-3 h-9 rounded text-[11px] font-bold uppercase tracking-widest bg-slate-200 text-slate-900 hover:bg-white disabled:opacity-40">
                                <FileText className="w-3.5 h-3.5" /> PDF
                            </button>
                            <button onClick={() => downloadCentre(FORMATS.xlsx)} disabled={!centreId} data-testid="download-centre-xlsx"
                                className="inline-flex items-center gap-2 px-3 h-9 rounded text-[11px] font-bold uppercase tracking-widest bg-[#00FF66] text-black hover:bg-[#00cc52] disabled:opacity-40">
                                <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
                            </button>
                        </div>
                        {centreSummary && (
                            <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="centre-summary">
                                <div className="bg-slate-950 border border-cyan-700/40 rounded p-3">
                                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Total Revenue</div>
                                    <div className="mt-1 font-display text-2xl font-bold text-cyan-300" data-testid="centre-total-revenue">₹{(centreSummary.total_revenue || 0).toLocaleString("en-IN")}</div>
                                    <div className="text-[10px] text-slate-500 mt-0.5">{centreSummary.total_bookings || 0} bookings</div>
                                </div>
                                <div className="bg-slate-950 border border-slate-800 rounded p-3">
                                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">User Paid</div>
                                    <div className="mt-1 font-display text-2xl font-bold text-white" data-testid="centre-user-paid">₹{(centreSummary.total_user_paid || 0).toLocaleString("en-IN")}</div>
                                </div>
                                <div className="bg-slate-950 border border-pink-700/40 rounded p-3">
                                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Admin Collected</div>
                                    <div className="mt-1 font-display text-2xl font-bold text-pink-300" data-testid="centre-admin-collected">₹{(centreSummary.total_admin_collected || 0).toLocaleString("en-IN")}</div>
                                </div>
                                <div className="bg-slate-950 border border-[#00FF66]/40 rounded p-3">
                                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Manager Collected</div>
                                    <div className="mt-1 font-display text-2xl font-bold text-[#00FF66]" data-testid="centre-manager-collected">₹{(centreSummary.total_manager_collected || 0).toLocaleString("en-IN")}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}
